import * as React from "react"
import { InvitationEmailResourceCreateInput } from "@modules/graphql/types"
import { useCreateInvitationForResourcesMutation } from "@modules/organization/contributors/queries.generated"
import {
  useShowSuccessToast,
  StyledPanelBoilerplate,
  StyledPanelBodySection,
} from "gatsby-interface"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { InviteMembersForm } from "../InviteMembersForm"
import { addMembers as addMembersText } from "@modules/locales/default.js"
import { useTracker, SegmentEventType } from "@modules/analytics"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import { memberFieldsToCloudResources } from "../utils"
import { WorkspaceInvitationsDocument } from "@modules/organization/contributors/queries.generated"
import { UsageData } from "@modules/organization/shared/hooks/useMembersUsage"

export type InviteMemberPanelModalProps = {
  isOpen: boolean
  closeModal: () => void
  organizationId: string
  usage: UsageData
}

export function InviteMemberPanelModal({
  isOpen,
  closeModal,
  organizationId,
  usage,
}: InviteMemberPanelModalProps) {
  const { trackSegment, trackAction } = useTracker()
  const [inviteMembers, { loading, errorAlert }] = useInviteMembers({
    organizationId,
  })

  const availableSeats = usage.all && usage.used && usage.all - usage.used

  return (
    <StyledPanelBoilerplate
      aria-label={addMembersText.headers.inviteMembers}
      header={addMembersText.headers.inviteMembers}
      isOpen={isOpen}
      onClose={closeModal}
    >
      {errorAlert && (
        <StyledPanelBodySection css={{ paddingBottom: 0 }}>
          {errorAlert}
        </StyledPanelBodySection>
      )}
      <InviteMembersForm
        organizationId={organizationId}
        onSubmit={invitees => {
          trackAction({
            eventType: `TRACK_EVENT`,
            name: `Sent invites for new members`,
            uiSource: `Members tab - "Add Members" modal`,
          })
          trackSegment({
            type: SegmentEventType.Track,
            event: `Sent invites for new members`,
            properties: {
              location: `Members tab - "Add Members" modal`,
            },
          })

          const invitationsData = invitees.map(invitee => ({
            email: invitee.email,
            resources: memberFieldsToCloudResources(organizationId, invitee),
          }))

          return inviteMembers(invitationsData, () => {
            closeModal()
            usage.recalculate()
          })
        }}
        onCancel={closeModal}
        loading={loading}
        availableSeats={availableSeats}
        isUnlimited={usage.isUnlimited}
      />
    </StyledPanelBoilerplate>
  )
}

function useInviteMembers({ organizationId }: { organizationId: string }) {
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()
  const [mutate, { loading }] = useCreateInvitationForResourcesMutation()

  const invite = (
    invitations: InvitationEmailResourceCreateInput[],
    onSuccess: () => void
  ) => {
    return mutate({
      variables: {
        invitations,
      },
      refetchQueries: [
        {
          query: WorkspaceInvitationsDocument,
          variables: {
            workspaceId: organizationId,
          },
        },
      ],
    })
      .then(() => {
        showSuccessToast(
          <FormattedMessage<never, "strong">
            message={addMembersText.messages.memberInvitationSent}
            tags={commonMessageTags}
          />
        )
        onSuccess()
      })
      .catch(err => setError(err))
  }

  return [invite, { loading, errorAlert }] as const
}
